import React from 'react';

import { Close, ContentCopy, Language, OpenInBrowser } from '@mui/icons-material';
import type { DialogProps } from '@mui/material';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  Slide,
  TextField,
} from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import { trim } from 'lodash';
import { createScopedI18n } from '../i18n/i18n';
import AndroidIcon from './icons/android';
import AppleIcon from './icons/apple';
import ChromeIcon from './icons/chrome';
import SafariIcon from './icons/safari';

const inAppBrowserBannerI18n = createScopedI18n('components.in_app_browser_banner');
const openInBrowserDialogI18n = createScopedI18n('components.open_in_browser_dialog');

const Transition = React.forwardRef(
  (props: TransitionProps & { children: React.ReactElement }, ref: React.Ref<unknown>) => (
    <Slide ref={ref} direction="up" {...props} />
  ),
);

export type OpenInBrowserDialogProps = {
  url?: string | URL | null;
  baseUrl?: string | URL | null;
  showInAppBrowserWarning?: boolean;
} & DialogProps;

const OpenInBrowserDialog = ({
  url,
  baseUrl,
  showInAppBrowserWarning,
  open,
  onClose,
  ...dialogProps
}: OpenInBrowserDialogProps) => {
  const targetUrl = url ?? window.location.href;
  const urlObject = React.useMemo(() => new URL(targetUrl, baseUrl ?? undefined), [targetUrl, baseUrl]);

  const { scheme, cleanUrl } = React.useMemo(
    () => ({
      scheme: trim(urlObject.protocol, ':') || 'https',
      cleanUrl: `${urlObject.host}${urlObject.pathname}${urlObject.search}${urlObject.hash}`,
    }),
    [urlObject],
  );

  const copyToClipboard = React.useCallback(() => {
    navigator.clipboard.writeText(urlObject.href);
  }, [urlObject]);

  const androidDefaultBrowserUri = React.useMemo(
    () => `intent://${cleanUrl}#Intent;scheme=${scheme};end`,
    [scheme, cleanUrl],
  );

  const iOSSafariUri = React.useMemo(() => `x-safari-https://${cleanUrl}`, [cleanUrl]);

  const iOSChromeUri = React.useMemo(() => {
    const chromeScheme = scheme === 'https' ? 'googlechromes' : 'googlechrome';
    return `${chromeScheme}://${cleanUrl}`;
  }, [scheme, cleanUrl]);

  const onCloseHandler = React.useCallback(() => {
    onClose?.({}, 'backdropClick');
  }, [onClose]);

  return (
    <Dialog open={open} onClose={onCloseHandler} fullScreen TransitionComponent={Transition} {...dialogProps}>
      <DialogTitle display="inline-flex" flexDirection="row" alignItems="center" gap={2}>
        <IconButton onClick={onCloseHandler}>
          <Close />
        </IconButton>

        {openInBrowserDialogI18n('title')}
      </DialogTitle>

      <DialogContent dividers>
        <Container maxWidth="sm" disableGutters sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {showInAppBrowserWarning ? (
            <Alert severity="warning" icon={<OpenInBrowser style={{ alignSelf: 'center' }} />}>
              {inAppBrowserBannerI18n('description')}
            </Alert>
          ) : null}

          <Card>
            <CardHeader
              avatar={<Language />}
              title={openInBrowserDialogI18n('url_card_title')}
              titleTypographyProps={{ variant: 'h6' }}
            />
            <CardContent sx={{ paddingTop: 0 }}>
              <TextField
                fullWidth
                value={urlObject.href}
                variant="outlined"
                size="small"
                inputProps={{ readOnly: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end" onClick={copyToClipboard} aria-label="copy url">
                        <ContentCopy />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader
              avatar={<AndroidIcon />}
              title={openInBrowserDialogI18n('android_card_title')}
              titleTypographyProps={{ variant: 'h6' }}
            />
            <CardContent sx={{ paddingTop: 0 }}>
              <Button
                LinkComponent={Link}
                variant="contained"
                disableElevation
                fullWidth
                href={androidDefaultBrowserUri}
                target="_blank"
                rel="noopener"
                startIcon={<OpenInBrowser />}
                sx={{ textTransform: 'none' }}
              >
                {openInBrowserDialogI18n('android_open_in_browser')}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader
              avatar={<AppleIcon />}
              title={openInBrowserDialogI18n('ios_card_title')}
              titleTypographyProps={{ variant: 'h6' }}
            />
            <CardContent sx={{ paddingTop: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                LinkComponent={Link}
                variant="contained"
                disableElevation
                fullWidth
                href={iOSSafariUri}
                target="_blank"
                rel="noopener"
                startIcon={<SafariIcon />}
                sx={{ textTransform: 'none' }}
              >
                {openInBrowserDialogI18n('ios_open_in_safari')}
              </Button>

              <Button
                LinkComponent={Link}
                variant="contained"
                disableElevation
                fullWidth
                href={iOSChromeUri}
                target="_blank"
                rel="noopener"
                startIcon={<ChromeIcon />}
                sx={{ textTransform: 'none' }}
              >
                {openInBrowserDialogI18n('ios_open_in_chrome')}
              </Button>
            </CardContent>
          </Card>
        </Container>
      </DialogContent>
    </Dialog>
  );
};

export default OpenInBrowserDialog;
